/**
 * EquipmentType React hooks — TanStack Query bindings.
 * STORY-1306 (Sprint 17 W3).
 */

import {
  createEquipmentType,
  getEquipmentType,
  listEquipmentTypes,
  updateEquipmentType,
  type CreateEquipmentTypeInput,
  type EquipmentType,
  type EquipmentTypeListFilter,
  type UpdateEquipmentTypeInput,
} from '@/api/equipment-types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const LIST_STALE_MS = 30_000;

export function useEquipmentTypes(filter: EquipmentTypeListFilter = {}) {
  const query = useQuery({
    queryKey: ['equipment-types', filter],
    queryFn: async () => {
      const res = await listEquipmentTypes(filter);
      return res.data;
    },
    // CR M8 patch: avoid refetch on focus + smooth list-flicker during filter changes.
    staleTime: LIST_STALE_MS,
    placeholderData: keepPreviousData,
  });

  return {
    types: (query.data ?? []) as EquipmentType[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useEquipmentType(id: string | null | undefined) {
  const query = useQuery({
    // CR M14 patch: use `'disabled'` sentinel key when id absent to avoid cache pollution.
    queryKey: id ? ['equipment-types', 'detail', id] : ['equipment-types', 'detail', 'disabled'],
    queryFn: async () => {
      // Guarded by `enabled: !!id`; non-null assertion safe here.
      const res = await getEquipmentType(id!);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    type: (query.data ?? null) as EquipmentType | null,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCreateEquipmentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEquipmentTypeInput) => createEquipmentType(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['equipment-types'] });
    },
  });
}

export function useUpdateEquipmentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEquipmentTypeInput }) =>
      updateEquipmentType(id, input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['equipment-types'] });
      void queryClient.invalidateQueries({ queryKey: ['equipment-types', 'detail', vars.id] });
    },
  });
}
